import { createSlice } from '@reduxjs/toolkit';
import { getDefaultView } from '@/pages/reports/non-ats-reports/shared/utils/date-group';
import { formatLocalDate } from '@/utils/dateUtils';

const reducerName = 'staticReports';

// Add initial table group defaults for different static report table IDs
const tableGroupDefaults = {
  pipeline_table: {
    group_by_1: {
      id: null,
      label: 'Time',
    },
    group_by_2: null,
    metrics: {
      id: 'passthrough_rate',
    },
  },
  live_job_pipeline_table: {
    group_by_1: null,
    group_by_2: null,
    metrics: { id: 'time_in_stage' },
  },
  interviews_table: {
    group_by_1: null,
    group_by_2: null,
  },
  offers_table: { group_by_1: null, group_by_2: null },
  hires_table: { group_by_1: null, group_by_2: null },
  rejections_table: { group_by_1: null, group_by_2: null },
  contacts_table: { group_by_1: null, group_by_2: null },
  jobs_table: { group_by_1: null, group_by_2: null },
  eeoc_race_table: { group_by_1: null, group_by_2: null },
  eeoc_veteran_table: { group_by_1: null, group_by_2: null },
  eeoc_disability_table: { group_by_1: null, group_by_2: null },
  recruiter_performance_table: { group_by_1: null, group_by_2: null },
  requisitions_table: { group_by_1: null, group_by_2: null },

  flow_health: { group_by_1: null, group_by_2: null, metrics: 'overall' },
  flow_user_activity: { group_by_1: null, group_by_2: null },
};

const initialState = {
  timeFrame: {
    date_group: 'Q',
    from: 'past12m',
    to: null,
  },

  filters: {},

  tableGroups: tableGroupDefaults, // Add table-specific group state

  drilldownData: {},
  drilldownIsLoading: {},
  chartTypes: {}, // Add chart types state
};

const { reducer, actions: staticReportActions } = createSlice({
  name: reducerName,
  initialState,
  reducers: {
    resetReportState: (state, { payload: { key } }) => {
      state[key] = { ...initialState[key] };
    },

    applyFilter: (state, { payload }) => {
      state.filters = payload;
    },

    // Update table-specific group
    applyTableGroup: (state, { payload: { tableId, groupKey, value } }) => {
      state.tableGroups[tableId] = {
        ...state.tableGroups[tableId],
        [groupKey]: value,
      };
    },

    applyTimeFrame: (state, { payload = {} }) => {
      const { from, to } = payload;
      const dateGroupValue = getDefaultView(from, to);
      if (!to) {
        state.timeFrame = {
          ...state.timeFrame,
          from: from,
          to: null,
          date_group: dateGroupValue,
        };
        return;
      }

      state.timeFrame = {
        ...state.timeFrame,
        from: formatLocalDate(from),
        to: formatLocalDate(to),
        date_group: dateGroupValue,
      };
    },

    clearFilter: (state, { payload }) => {
      const { key } = payload;
      state.filters[key] = initialState.filters[key];
    },

    clearAllFilter: (state) => {
      state.filters = { ...initialState.filters };
    },

    applyViewBy: (state, { payload = {} }) => {
      const { id } = payload;
      state.timeFrame = {
        ...state.timeFrame,
        date_group: id,
      };
    },

    setDrilldownData: (state, { payload: { tableId, data } }) => {
      state.drilldownData[tableId] = data;
      state.drilldownIsLoading[tableId] = false;
    },

    setDrilldownLoading: (state, { payload: { tableId, isLoading } }) => {
      state.drilldownIsLoading[tableId] = isLoading;
    },

    resetDrilldownData: (state, { payload: { tableId } }) => {
      state.drilldownData[tableId] = [];
      state.drilldownIsLoading[tableId] = false;
    },

    // Add chart type actions
    setChartType: (state, { payload: { chartKey, chartType } }) => {
      state.chartTypes[chartKey] = chartType;
    },
  },
});

export { staticReportActions };
export default reducer;
